import { components, paths } from "@/api-schema";
import { DataExportType, PathsV1CompanyGroupSearchPostParametersQueryOrderByField } from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useCompanyGroupDeleteMutation } from "@/common/api/company-group/use-company-group-delete.mutation";
import { useCompanyGroupSearchQuery } from "@/common/api/company-group/use-company-group-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { ConfirmationType } from "@/components/confirm-dialog/_types";
import { DeleteConfirmDialog } from "@/components/confirm-dialog/predefined/delete-confirm-dialog";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures, TimestampSortField } from "@/components/data-table/types";
import i18next from "i18next";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  BaseSorting,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { companyGroupTableDefaultColumns } from "./company-group-table-columns";

type CompanyGroup = components["schemas"]["CompanyGroup"] & RowWithIdentifier;
type CompanyGroupQueryParams = Omit<
  NonNullable<paths["/v1/company-group/search"]["post"]["parameters"]["query"]>,
  "orderBy"
> & { orderBy?: BaseSorting };
type CompanyGroupRequestBody = paths["/v1/company-group/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [companyGroupTableDefaultColumns.columnName, companyGroupTableDefaultColumns.columnTechnicalId];

const COMPANY_GROUP_TABLE_CONFIG: PredefinedTableConfig<BaseSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [{ field: "name", direction: "asc" }],
  defaultTableConfig: {
    searchPlaceholder: i18next.t("masterdata:SearchCompanyGroups"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: true,
  } as const,
  sortingMapping: {
    name: ["name"],
    technicalId: ["technicalId"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
  sortColumns: [
    { id: PathsV1CompanyGroupSearchPostParametersQueryOrderByField.Name, label: () => i18next.t("common:Name") },
    {
      id: PathsV1CompanyGroupSearchPostParametersQueryOrderByField.TechnicalId,
      label: () => i18next.t("common:TechnicalId"),
    },
  ],
  timestampSortFields: [TimestampSortField.CreatedAt, TimestampSortField.UpdatedAt],
  exportType: DataExportType.CompanyGroup,
} as const;

const COMPANY_GROUP_TABLE_QUERY_HOOKS: DataFetchHooks<CompanyGroup, CompanyGroupQueryParams, CompanyGroupRequestBody> =
  {
    useSearchQuery: useCompanyGroupSearchQuery,
  } as const;

export interface CompanyGroupTableProps extends BasePredefinedTableProps<
  CompanyGroup,
  BaseSorting,
  CompanyGroupQueryParams,
  CompanyGroupRequestBody
> {}

const BaseCompanyGroupTable = createBasePredefinedTable({
  config: COMPANY_GROUP_TABLE_CONFIG,
  queryHooks: COMPANY_GROUP_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function CompanyGroupTable(props: CompanyGroupTableProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const {
    name = "company-group-table",
    createButtonText = t("masterdata:AddCompanyGroup"),
    showCreateButton = true,
    showExportButton = true,
    ...restProps
  } = props;

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedCompanyGroup, setSelectedCompanyGroup] = useState<CompanyGroup | null>(null);
  const deleteMutation = useCompanyGroupDeleteMutation();

  const handleDeleteClick = useCallback((companyGroup: CompanyGroup) => {
    setSelectedCompanyGroup(companyGroup);
    setConfirmDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (selectedCompanyGroup !== null) {
      await toastMutationStatus({
        mutationFn: () => deleteMutation.mutateAsync({ uuid: selectedCompanyGroup.uuid }),
        successMessage: t("masterdata:CompanyGroupDeleted", { name: selectedCompanyGroup.name }),
        errorMessage: t("masterdata:ErrorDeletingCompanyGroup", { name: selectedCompanyGroup.name }),
      });
    }
    setConfirmDialogOpen(false);
  }, [selectedCompanyGroup, deleteMutation, t]);

  return (
    <>
      <BaseCompanyGroupTable
        searchPlaceholder={t("masterdata:SearchCompanyGroups")}
        name={name}
        createButtonText={createButtonText}
        showCreateButton={showCreateButton}
        showExportButton={showExportButton}
        onDelete={handleDeleteClick}
        {...restProps}
      />

      <DeleteConfirmDialog
        open={confirmDialogOpen}
        label={t("masterdata:DeleteCompanyGroup")}
        name={selectedCompanyGroup?.name ?? ""}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        requireConfirmation={true}
        confirmationType={ConfirmationType.Name}
        showIrreversibleWarning={true}
      />
    </>
  );
}
